import { useState, useRef, useCallback, useEffect } from "react";
import config from '../config';
import Swal from 'sweetalert2';

// ─── Config ───────────────────────────────────────────────────
const API = `${config.API_BASE_URL}/Flowchart`;

// ─── Get current logged-in username from localStorage ─────────
const getLoggedInUser = () => {
  try {
    // Try user first (regular / Google login)
    const user = JSON.parse(localStorage.getItem("user") || "null");
    if (user?.username) return { username: user.username, fullName: user.fullNames || user.username, role: "user" };
    // Then admin / superadmin
    const admin = JSON.parse(localStorage.getItem("admin") || "null");
    if (admin?.username) return { username: admin.username, fullName: admin.fullNames || admin.username, role: admin.role || "admin" };
  } catch (e) {}
  return { username: "guest", fullName: "Guest", role: "guest" };
};

// ─── SweetAlert2 presets (dark theme matching site) ───────────
const swal = {
  success: (msg) => Swal.fire({
    toast: true, position: "bottom-end", icon: "success",
    title: msg, showConfirmButton: false, timer: 2500, timerProgressBar: true,
    background: "#161b22", color: "#e6edf3", iconColor: "#22c55e",
    didOpen: (t) => { t.addEventListener("mouseenter", Swal.stopTimer); t.addEventListener("mouseleave", Swal.resumeTimer); },
  }),
  error: (msg) => Swal.fire({
    toast: true, position: "bottom-end", icon: "error",
    title: msg, showConfirmButton: false, timer: 3500, timerProgressBar: true,
    background: "#161b22", color: "#e6edf3", iconColor: "#ef4444",
  }),
  warning: (msg) => Swal.fire({
    toast: true, position: "bottom-end", icon: "warning",
    title: msg, showConfirmButton: false, timer: 2500, timerProgressBar: true,
    background: "#161b22", color: "#e6edf3", iconColor: "#f59e0b",
  }),
  info: (msg) => Swal.fire({
    toast: true, position: "bottom-end", icon: "info",
    title: msg, showConfirmButton: false, timer: 2000, timerProgressBar: true,
    background: "#161b22", color: "#e6edf3", iconColor: "#3b82f6",
  }),
  confirmDelete: (itemTitle, text) => Swal.fire({
    title: `Delete "${itemTitle}"?`,
    text: text || "This item will be moved to trash.",
    icon: "warning",
    showCancelButton: true,
    confirmButtonText: "Yes, delete it",
    cancelButtonText: "Cancel",
    confirmButtonColor: "#ef4444",
    cancelButtonColor: "#30363d",
    background: "#161b22", color: "#e6edf3", iconColor: "#f59e0b",
    reverseButtons: true,
  }),
  confirmRestore: (itemTitle) => Swal.fire({
    title: "Restore Flowchart?",
    text: `"${itemTitle}" will be moved back from trash.`,
    icon: "question",
    showCancelButton: true,
    confirmButtonText: "Yes, restore",
    cancelButtonText: "Cancel",
    confirmButtonColor: "#22c55e",
    cancelButtonColor: "#30363d",
    background: "#161b22", color: "#e6edf3", iconColor: "#22c55e",
    reverseButtons: true,
  }),
  confirmLoad: (itemTitle) => Swal.fire({
    title: "Load this flowchart?",
    text: `"${itemTitle}" — any unsaved changes will be lost.`,
    icon: "question",
    showCancelButton: true,
    confirmButtonText: "Load it",
    cancelButtonText: "Cancel",
    confirmButtonColor: "#3b82f6",
    cancelButtonColor: "#30363d",
    background: "#161b22", color: "#e6edf3", iconColor: "#3b82f6",
    reverseButtons: true,
  }),
  confirmClear: () => Swal.fire({
    title: "Clear Canvas?",
    text: "All unsaved nodes and edges will be removed.",
    icon: "warning",
    showCancelButton: true,
    confirmButtonText: "Yes, clear it",
    cancelButtonText: "Cancel",
    confirmButtonColor: "#ef4444",
    cancelButtonColor: "#30363d",
    background: "#161b22", color: "#e6edf3", iconColor: "#f59e0b",
    reverseButtons: true,
  }),
  confirmDeleteFolder: (folderName) => Swal.fire({
    title: `Delete folder "${folderName}"?`,
    text: "Flowcharts inside will not be deleted.",
    icon: "warning",
    showCancelButton: true,
    confirmButtonText: "Yes, delete folder",
    cancelButtonText: "Cancel",
    confirmButtonColor: "#ef4444",
    cancelButtonColor: "#30363d",
    background: "#161b22", color: "#e6edf3", iconColor: "#f59e0b",
    reverseButtons: true,
  }),
  inputFolder: () => Swal.fire({
    title: "New Folder",
    input: "text",
    inputPlaceholder: "Enter folder name...",
    showCancelButton: true,
    confirmButtonText: "Create",
    cancelButtonText: "Cancel",
    confirmButtonColor: "#3b82f6",
    cancelButtonColor: "#30363d",
    background: "#161b22", color: "#e6edf3",
    inputAttributes: { autocomplete: "off" },
    customClass: { input: "swal-input-dark" },
    inputValidator: (v) => !v?.trim() ? "Folder name cannot be empty!" : null,
  }),
  inputEdgeLabel: (current) => Swal.fire({
    title: "Edge Label",
    input: "text",
    inputValue: current || "",
    inputPlaceholder: "Optional label...",
    showCancelButton: true,
    confirmButtonText: "Save",
    cancelButtonText: "Cancel",
    confirmButtonColor: "#3b82f6",
    cancelButtonColor: "#30363d",
    background: "#161b22", color: "#e6edf3",
    inputAttributes: { autocomplete: "off" },
  }),
};

const STORAGE_KEY = "flowcraft_v3";

// ─── Theme ────────────────────────────────────────────────────
const T = {
  bg:      "#0f1117",
  surface: "#161b22",
  panel:   "#1c2333",
  border:  "#21262d",
  accent:  "#3b82f6",
  success: "#22c55e",
  danger:  "#ef4444",
  warning: "#f59e0b",
  text:    "#e6edf3",
  muted:   "#8b949e",
  subtle:  "#30363d",
};

const NODE_W = 160, NODE_H = 60;

const NODE_TYPES = {
  start:    { label:"Start",    icon:"▶" },
  end:      { label:"End",      icon:"■" },
  process:  { label:"Process",  icon:"□" },
  decision: { label:"Decision", icon:"◆" },
  data:     { label:"Data I/O", icon:"⬡" },
  document: { label:"Document", icon:"📄" },
};

const COLOR_PRESETS = [
  "#8b949e","#3b82f6","#22c55e","#ef4444",
  "#f59e0b","#06b6d4","#8b5cf6","#ec4899",
  "#ffffff","#f97316","#a3e635","#2dd4bf",
];

// ─── Helpers ──────────────────────────────────────────────────
function getShapePath(node) {
  const { x, y } = node, w = NODE_W, h = NODE_H;
  switch (node.type) {
    case "start": case "end":
      return `M${x+h/2},${y} L${x+w-h/2},${y} Q${x+w},${y} ${x+w},${y+h/2} Q${x+w},${y+h} ${x+w-h/2},${y+h} L${x+h/2},${y+h} Q${x},${y+h} ${x},${y+h/2} Q${x},${y} ${x+h/2},${y} Z`;
    case "decision":
      return `M${x+w/2},${y} L${x+w},${y+h/2} L${x+w/2},${y+h} L${x},${y+h/2} Z`;
    case "data":
      return `M${x+18},${y} L${x+w},${y} L${x+w-18},${y+h} L${x},${y+h} Z`;
    case "document":
      return `M${x},${y} L${x+w},${y} L${x+w},${y+h-14} Q${x+w*0.75},${y+h} ${x+w/2},${y+h-10} Q${x+w*0.25},${y+h-20} ${x},${y+h-10} Z`;
    default:
      return `M${x+8},${y} L${x+w-8},${y} Q${x+w},${y} ${x+w},${y+8} L${x+w},${y+h-8} Q${x+w},${y+h} ${x+w-8},${y+h} L${x+8},${y+h} Q${x},${y+h} ${x},${y+h-8} L${x},${y+8} Q${x},${y} ${x+8},${y} Z`;
  }
}

const getCenter = n => ({ x: n.x + NODE_W/2, y: n.y + NODE_H/2 });
const hitTest   = (n,px,py) => px>=n.x && px<=n.x+NODE_W && py>=n.y && py<=n.y+NODE_H;
let idC = 100;
const uid = () => String(Date.now()) + String(++idC);

// ─── FIX #1: Normalize label lines — handle both \n literal and real newlines ─
const splitLabel = (label) => label ? label.split(/\\n|\n/) : [""];
// When saving: normalize actual newlines → literal \n for consistent storage
const normalizeLabel = (raw) => raw.replace(/\n/g, "\\n");

const mkBtn = (color, outline) => ({
  padding:"7px 14px", borderRadius:6, fontFamily:"'Inter','Segoe UI',sans-serif",
  fontSize:12, fontWeight:600, cursor:"pointer", transition:"all 0.12s",
  border:`1px solid ${color||T.border}`,
  background: outline ? "transparent" : color||T.panel,
  color: outline ? color : ["#ef4444","#3b82f6","#22c55e","#f59e0b"].includes(color) ? "#fff" : T.text,
});

// ─── Arrow ────────────────────────────────────────────────────
function Arrow({ edge, nodes, selected, onSelect, onDoubleClick }) {
  const fn = nodes.find(n=>n.id===edge.from), tn = nodes.find(n=>n.id===edge.to);
  if (!fn||!tn) return null;
  const fc=getCenter(fn), tc=getCenter(tn), my=(fc.y+tc.y)/2;
  const path=`M${fc.x},${fc.y} C${fc.x},${my} ${tc.x},${my} ${tc.x},${tc.y}`;
  const ang=Math.atan2(tc.y-fc.y,tc.x-fc.x)*180/Math.PI+90;
  const col=selected?T.accent:T.subtle;
  const midX=(fc.x+tc.x)/2, midY=(fc.y+tc.y)/2;
  return (
    <g>
      {/* Wide invisible hit area */}
      <path d={path} stroke="transparent" strokeWidth={18} fill="none"
        style={{cursor:"pointer"}}
        onClick={e=>{e.stopPropagation();onSelect(edge.id);}}
        onDoubleClick={e=>{e.stopPropagation();onDoubleClick(edge.id, edge.label);}}/>
      <path d={path} stroke={col} strokeWidth={selected?2.5:1.5} fill="none" style={{pointerEvents:"none"}}/>
      <polygon points="0,-10 7,4 -7,4" fill={col} transform={`translate(${tc.x},${tc.y}) rotate(${ang})`} style={{pointerEvents:"none"}}/>
      {edge.label && (
        <g style={{pointerEvents:"none"}}>
          <rect x={midX-edge.label.length*3.5-6} y={midY-10} width={edge.label.length*7+12} height={18}
            rx={4} fill={T.surface} stroke={T.border} strokeWidth={1}/>
          <text x={midX} y={midY+2} textAnchor="middle" fill={T.muted} fontSize={11} fontFamily="monospace">{edge.label}</text>
        </g>
      )}
    </g>
  );
}

// ─── FlowNode ─────────────────────────────────────────────────
function FlowNode({ node, selected, onPointerDown, onDoubleClick }) {
  const col = node.color||"#8b949e";
  // FIX #1 applied here
  const lines = splitLabel(node.label);
  return (
    <g
      onPointerDown={e=>onPointerDown(e,node)}
      onDoubleClick={e=>{e.stopPropagation();onDoubleClick(node.id);}}
      style={{cursor:"grab"}}>
      <path d={getShapePath(node)} fill={selected?col+"28":col+"12"} stroke={col} strokeWidth={selected?2.5:1.5}
        style={{filter:selected?`drop-shadow(0 0 8px ${col}88)`:"drop-shadow(0 2px 4px #00000066)"}}/>
      {lines.map((line,i)=>(
        <text key={i}
          x={node.x+NODE_W/2}
          y={node.y+NODE_H/2+(i-(lines.length-1)/2)*16}
          textAnchor="middle" dominantBaseline="middle"
          fill={col} fontSize={12} fontWeight={selected?600:400}
          fontFamily="'Inter','Segoe UI',sans-serif"
          style={{pointerEvents:"none",userSelect:"none"}}>
          {line}
        </text>
      ))}
    </g>
  );
}

// ─── Color Picker ─────────────────────────────────────────────
function ColorPicker({ current, onChange }) {
  const [custom, setCustom] = useState(current||"#8b949e");
  useEffect(()=>setCustom(current||"#8b949e"),[current]);
  return (
    <div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:5,marginBottom:8}}>
        {COLOR_PRESETS.map(c=>(
          <div key={c} onClick={()=>onChange(c)} title={c}
            style={{aspectRatio:"1",borderRadius:5,background:c,cursor:"pointer",boxSizing:"border-box",
              border:current===c?`2px solid ${T.text}`:`2px solid ${T.border}`,
              boxShadow:current===c?`0 0 6px ${c}99`:"none",transition:"all 0.1s"}}/>
        ))}
      </div>
      <div style={{display:"flex",gap:5}}>
        <input type="color" value={custom} onChange={e=>setCustom(e.target.value)}
          style={{width:32,height:28,border:"none",background:"transparent",cursor:"pointer",padding:0,borderRadius:4}}/>
        <input type="text" value={custom} onChange={e=>setCustom(e.target.value)}
          onKeyDown={e=>e.key==="Enter"&&onChange(custom)}
          style={{flex:1,background:T.panel,border:`1px solid ${T.border}`,borderRadius:5,color:T.text,padding:"4px 8px",fontSize:11,fontFamily:"monospace",outline:"none"}}
          placeholder="#3b82f6"/>
        <button onClick={()=>onChange(custom)}
          style={{padding:"4px 10px",borderRadius:5,border:`1px solid ${T.border}`,background:T.accent,color:"#fff",cursor:"pointer",fontSize:11,fontWeight:600}}>✔</button>
      </div>
    </div>
  );
}

// ─── Save Modal ───────────────────────────────────────────────
function SaveModal({ nodes, edges, folders, currentId, currentTitle, onClose, onSaved }) {
  const [title, setTitle]     = useState(currentTitle || "Untitled Flowchart");
  const [desc, setDesc]       = useState("");
  const [folderId, setFolder] = useState("");
  const [loading, setLoading] = useState(false);

  const doSave = async () => {
    if (!title.trim()) return swal.warning("Please enter a title!");
    setLoading(true);
    const body = {
      title: title.trim(),
      description: desc,
      folderId: folderId ? Number(folderId) : null,
      username: getLoggedInUser().username,
      nodes: nodes.map(n => ({ nodeKey:n.id, type:n.type, label:n.label, color:n.color||"#8b949e", x:n.x, y:n.y })),
      edges: edges.map(e => ({ edgeKey:e.id, fromNodeKey:e.from, toNodeKey:e.to, label:e.label||"" })),
    };
    try {
      const method = currentId ? "PUT" : "POST";
      const url    = currentId ? `${API}/${currentId}` : API;
      const res    = await fetch(url, { method, headers:{"Content-Type":"application/json"}, body:JSON.stringify(body) });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      swal.success("Flowchart saved!");
      onSaved(data.id, title.trim());
      onClose();
    } catch(e) {
      swal.error("Save failed: " + e.message);
    } finally { setLoading(false); }
  };

  return (
    <ModalWrap onClose={onClose} title="💾 Save Flowchart">
      <label style={LS.label}>Title *</label>
      <input style={LS.input} value={title} onChange={e=>setTitle(e.target.value)} placeholder="My Flowchart" autoFocus/>
      <label style={LS.label}>Description</label>
      <textarea style={{...LS.input,minHeight:60,resize:"vertical"}} value={desc} onChange={e=>setDesc(e.target.value)} placeholder="Optional description..."/>
      <label style={LS.label}>Folder</label>
      <select style={LS.input} value={folderId} onChange={e=>setFolder(e.target.value)}>
        <option value="">📂 No folder (root)</option>
        {folders.map(f=><option key={f.id} value={f.id}>📁 {f.name}</option>)}
      </select>
      <div style={{display:"flex",gap:8,marginTop:16}}>
        <button style={{...mkBtn(T.accent),flex:1}} onClick={doSave} disabled={loading}>
          {loading ? "Saving..." : currentId ? "🔄 Update" : "💾 Save"}
        </button>
        <button style={{...mkBtn(),flex:1}} onClick={onClose}>Cancel</button>
      </div>
    </ModalWrap>
  );
}

// ─── Load / Library Modal ─────────────────────────────────────
function LoadModal({ onClose, onLoad }) {
  const [tab, setTab]           = useState("all");
  const [list, setList]         = useState([]);
  const [folders, setFolders]   = useState([]);
  const [trash, setTrash]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [selFolder, setSelFol]  = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [fc, fo, tr] = await Promise.all([
        fetch(API).then(r=>r.json()),
        fetch(`${API}/folders`).then(r=>r.json()),
        fetch(`${API}/deleted`).then(r=>r.json()),
      ]);
      setList(fc); setFolders(fo); setTrash(tr);
    } catch(e) { swal.error("Failed to load: "+e.message); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const loadFlowchart = async (id, title) => {
    const result = await swal.confirmLoad(title);
    if (!result.isConfirmed) return;
    try {
      const res  = await fetch(`${API}/${id}`);
      const data = await res.json();
      onLoad(data);
      onClose();
      swal.success(`"${title}" loaded!`);
    } catch(e) { swal.error("Load failed: "+e.message); }
  };

  const deleteFC = async (id, title) => {
    const result = await swal.confirmDelete(title);
    if (!result.isConfirmed) return;
    try {
      await fetch(`${API}/${id}`, {method:"DELETE"});
      swal.success("Moved to trash.");
      load();
    } catch(e) { swal.error(e.message); }
  };

  const restoreFC = async (id, title) => {
    const result = await swal.confirmRestore(title);
    if (!result.isConfirmed) return;
    try {
      await fetch(`${API}/${id}/restore`, {method:"PUT"});
      swal.success(`"${title}" has been restored!`);
      load();
    } catch(e) { swal.error(e.message); }
  };

  const createFolder = async () => {
    const result = await swal.inputFolder();
    if (!result.isConfirmed || !result.value?.trim()) return;
    try {
      await fetch(`${API}/folders`, {method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({name:result.value.trim(),username:getLoggedInUser().username})});
      load();
      swal.success(`Folder "${result.value.trim()}" created!`);
    } catch(e) { swal.error(e.message); }
  };

  const deleteFolder = async (id, name) => {
    const result = await swal.confirmDeleteFolder(name);
    if (!result.isConfirmed) return;
    try {
      await fetch(`${API}/folders/${id}`, {method:"DELETE"});
      swal.success(`Folder "${name}" deleted.`);
      load();
    } catch(e) { swal.error(e.message); }
  };

  const filtered = list;

  return (
    <ModalWrap onClose={onClose} title="📂 Flowchart Library" wide>
      <div style={{display:"flex",gap:4,marginBottom:16,borderBottom:`1px solid ${T.border}`,paddingBottom:10}}>
        {[["all","All"],["folders","Folders"],["trash","🗑 Trash"]].map(([k,l])=>(
          <button key={k} onClick={()=>setTab(k)}
            style={{...mkBtn(tab===k?T.accent:undefined,tab!==k),padding:"5px 14px",fontSize:12}}>
            {l}
          </button>
        ))}
        <div style={{flex:1}}/>
        <button onClick={load} style={{...mkBtn(),padding:"5px 10px",fontSize:12}}>⟳ Refresh</button>
      </div>

      {loading ? (
        <div style={{textAlign:"center",padding:40,color:T.muted}}>Loading...</div>
      ) : tab==="trash" ? (
        <div>
          {trash.length===0 ? <Empty label="Trash is empty"/> : trash.map(f=>(
            <div key={f.id} style={LS.row}>
              <div>
                <div style={{fontSize:13,color:T.muted,fontWeight:500}}>{f.title}</div>
                <div style={{fontSize:10,color:T.subtle}}>Deleted {new Date(f.modifiedAt).toLocaleDateString()}</div>
              </div>
              <button onClick={()=>restoreFC(f.id,f.title)} style={{...mkBtn(T.success),padding:"4px 12px",fontSize:11}}>↩ Restore</button>
            </div>
          ))}
        </div>
      ) : tab==="folders" ? (
        <div>
          <div style={{display:"flex",justifyContent:"flex-end",marginBottom:12}}>
            <button style={{...mkBtn(T.accent),padding:"6px 16px",fontSize:12}} onClick={createFolder}>
              📁 + New Folder
            </button>
          </div>
          {folders.length===0 ? <Empty label="No folders yet"/> : folders.map(fo=>(
            <div key={fo.id} style={{...LS.row,flexDirection:"column",alignItems:"flex-start",gap:8}}>
              <div style={{display:"flex",width:"100%",alignItems:"center"}}>
                <span style={{fontSize:16,marginRight:8}}>📁</span>
                <div style={{flex:1}}>
                  <div style={{fontSize:13,fontWeight:600,color:T.text}}>{fo.name}</div>
                  <div style={{fontSize:10,color:T.muted}}>{fo.flowchartCount} flowchart(s)</div>
                </div>
                <div style={{display:"flex",gap:5}}>
                  <button onClick={()=>setSelFol(selFolder===fo.id?null:fo.id)}
                    style={{...mkBtn(T.accent,true),padding:"3px 10px",fontSize:11}}>
                    {selFolder===fo.id?"▲ Hide":"▼ View"}
                  </button>
                  <button onClick={()=>deleteFolder(fo.id,fo.name)}
                    style={{...mkBtn(T.danger,true),padding:"3px 10px",fontSize:11}}>🗑</button>
                </div>
              </div>
              {selFolder===fo.id && (
                <div style={{width:"100%",paddingLeft:24,borderLeft:`2px solid ${T.border}`}}>
                  {list.filter(f=>f.folderId===fo.id).length===0
                    ? <div style={{fontSize:11,color:T.muted,padding:"8px 0"}}>No flowcharts in this folder</div>
                    : list.filter(f=>f.folderId===fo.id).map(f=>(
                      <FCRow key={f.id} fc={f} onLoad={loadFlowchart} onDelete={deleteFC}/>
                    ))
                  }
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div>
          {filtered.length===0 ? <Empty label="No saved flowcharts yet"/> : filtered.map(f=>(
            <FCRow key={f.id} fc={f} onLoad={loadFlowchart} onDelete={deleteFC}/>
          ))}
        </div>
      )}
    </ModalWrap>
  );
}

function FCRow({ fc, onLoad, onDelete }) {
  return (
    <div style={LS.row}>
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontSize:13,fontWeight:600,color:T.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
          {fc.title}
        </div>
        <div style={{fontSize:10,color:T.muted,marginTop:2}}>
          {fc.folderName ? `📁 ${fc.folderName} · ` : ""}
          {fc.nodeCount||0} nodes · {fc.edgeCount||0} edges · {new Date(fc.modifiedAt).toLocaleDateString()}
        </div>
      </div>
      <div style={{display:"flex",gap:5,flexShrink:0}}>
        <button onClick={()=>onLoad(fc.id, fc.title)} style={{...mkBtn(T.accent),padding:"4px 12px",fontSize:11}}>📂 Load</button>
        <button onClick={()=>onDelete(fc.id,fc.title)} style={{...mkBtn(T.danger,true),padding:"4px 10px",fontSize:11}}>🗑</button>
      </div>
    </div>
  );
}

function ModalWrap({ onClose, title, children, wide }) {
  return (
    <div style={{position:"fixed",inset:0,background:"#00000088",display:"flex",alignItems:"center",justifyContent:"center",zIndex:300}}
      onClick={onClose}>
      <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:12,padding:24,
          width:wide?"640px":"380px",maxWidth:"95vw",maxHeight:"85vh",overflow:"auto",
          boxShadow:"0 24px 64px #000000bb"}}
        onClick={e=>e.stopPropagation()}>
        <div style={{display:"flex",alignItems:"center",marginBottom:18}}>
          <span style={{fontSize:15,fontWeight:700,color:T.accent,flex:1}}>{title}</span>
          <button onClick={onClose} style={{background:"none",border:"none",color:T.muted,cursor:"pointer",fontSize:18,lineHeight:1}}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Empty({ label }) {
  return <div style={{textAlign:"center",padding:"32px 0",color:T.subtle,fontSize:13}}>{label}</div>;
}

const LS = {
  label: { display:"block", fontSize:9, color:T.muted, textTransform:"uppercase", letterSpacing:2, marginBottom:5, marginTop:12 },
  input: { width:"100%", background:T.panel, border:`1px solid ${T.border}`, borderRadius:6, color:T.text, padding:"7px 10px", fontSize:12, fontFamily:"'Inter',sans-serif", outline:"none", boxSizing:"border-box" },
  row:   { display:"flex", alignItems:"center", gap:10, padding:"10px 12px", borderRadius:8, border:`1px solid ${T.border}`, background:T.panel, marginBottom:8 },
};

// ═══════════════════════════════════════════════════════════════
//  MAIN APP
// ═══════════════════════════════════════════════════════════════
export default function App() {
  const loadLocal = () => {
    try {
      const r=window.localStorage.getItem(STORAGE_KEY);
      if(r){ const d=JSON.parse(r); return{nodes:d.nodes||[],edges:d.edges||[]}; }
    } catch(e){}
    return {nodes:[],edges:[]};
  };

  const init = loadLocal();
  const [nodes, setNodes]       = useState(init.nodes);
  const [edges, setEdges]       = useState(init.edges);
  const [selNode, setSelNode]   = useState(null);
  const [selEdge, setSelEdge]   = useState(null);
  const [drag, setDrag]         = useState(null);
  const [connecting, setConn]   = useState(null);
  const [connectMode, setConnectMode] = useState(false); // FIX #2: toggle connect mode
  const [mouse, setMouse]       = useState({x:0,y:0});
  const [zoom, setZoom]         = useState(1);
  const [pan, setPan]           = useState({x:60,y:40});
  const [panDrag, setPanDrag]   = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [editVal, setEditVal]   = useState("");
  const [localSaved, setLocalSaved] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // ── Undo/Redo stacks ──────────────────────────────────────
  const [history, setHistory]   = useState([{nodes:init.nodes, edges:init.edges}]);
  const [histIdx, setHistIdx]   = useState(0);

  const pushHistory = useCallback((newNodes, newEdges) => {
    setHistory(h => {
      const trimmed = h.slice(0, histIdx+1);
      return [...trimmed, {nodes:newNodes, edges:newEdges}].slice(-50); // max 50 steps
    });
    setHistIdx(i => Math.min(i+1, 49));
  }, [histIdx]);

  const undo = useCallback(() => {
    setHistIdx(i => {
      if (i <= 0) { swal.warning("Nothing to undo."); return i; }
      const prev = history[i-1];
      setNodes(prev.nodes); setEdges(prev.edges);
      setSelNode(null); setSelEdge(null);
      swal.info("Undone!");
      return i-1;
    });
  }, [history]);

  const redo = useCallback(() => {
    setHistIdx(i => {
      if (i >= history.length-1) { swal.warning("Nothing to redo."); return i; }
      const next = history[i+1];
      setNodes(next.nodes); setEdges(next.edges);
      setSelNode(null); setSelEdge(null);
      swal.info("Redone!");
      return i+1;
    });
  }, [history]);

  // ── DB state ──────────────────────────────────────────────
  const [currentId, setCurrentId]       = useState(null);
  const [currentTitle, setCurrentTitle] = useState("Untitled Flowchart");
  const [folders, setFolders]           = useState([]);
  const [showSave, setShowSave]         = useState(false);
  const [showLoad, setShowLoad]         = useState(false);

  const svgRef  = useRef(null);
  const rootRef = useRef(null);

  // ── Current logged-in user ─────────────────────────────────
  const loggedInUser = getLoggedInUser();

  // ── Local auto-save ────────────────────────────────────────
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY,JSON.stringify({nodes,edges}));
      setLocalSaved(true);
      const t=setTimeout(()=>setLocalSaved(false),1000);
      return()=>clearTimeout(t);
    } catch(e){}
  }, [nodes,edges]);

  // ── Load folders on mount ──────────────────────────────────
  useEffect(() => {
    fetch(`${API}/folders`).then(r=>r.json()).then(setFolders).catch(()=>{});
  }, []);

  // ── Fullscreen ─────────────────────────────────────────────
  const toggleFullscreen = () => {
    if (!document.fullscreenElement)
      rootRef.current?.requestFullscreen().then(()=>setIsFullscreen(true)).catch(()=>{});
    else
      document.exitFullscreen().then(()=>setIsFullscreen(false)).catch(()=>{});
  };
  useEffect(() => {
    const fn=()=>setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange",fn);
    return()=>document.removeEventListener("fullscreenchange",fn);
  },[]);

  // ── Load from API ──────────────────────────────────────────
  const handleLoad = (data) => {
    const newNodes = (data.nodes||[]).map(n=>({
      id:n.nodeKey, type:n.type, label:n.label, color:n.color, x:n.x, y:n.y
    }));
    const newEdges = (data.edges||[]).map(e=>({
      id:e.edgeKey, from:e.fromNodeKey, to:e.toNodeKey, label:e.label||""
    }));
    setNodes(newNodes); setEdges(newEdges);
    setCurrentId(data.id); setCurrentTitle(data.title);
    setSelNode(null); setSelEdge(null);
    pushHistory(newNodes, newEdges);
  };

  // ── Add node ───────────────────────────────────────────────
  const addNode = type => {
    const el=svgRef.current, cx=el?el.clientWidth/2:400, cy=el?el.clientHeight/2:300, id=uid();
    const newNode = {
      id, type,
      x:(cx-pan.x)/zoom-NODE_W/2+(Math.random()-.5)*120,
      y:(cy-pan.y)/zoom-NODE_H/2+(Math.random()-.5)*100,
      label:NODE_TYPES[type].label,
      color:"#8b949e"
    };
    const newNodes = [...nodes, newNode];
    setNodes(newNodes);
    setSelNode(id); setSelEdge(null);
    pushHistory(newNodes, edges);
    swal.info(`Added ${NODE_TYPES[type].label}`);
  };

  // ── Duplicate node (FIX #3) ────────────────────────────────
  const duplicateNode = useCallback(() => {
    if (!selNode) return;
    const src = nodes.find(n=>n.id===selNode);
    if (!src) return;
    const id = uid();
    const newNode = { ...src, id, x:src.x+30, y:src.y+30 };
    const newNodes = [...nodes, newNode];
    setNodes(newNodes);
    setSelNode(id);
    pushHistory(newNodes, edges);
    swal.success("Node duplicated!");
  }, [selNode, nodes, edges, pushHistory]);

  // ── Delete ─────────────────────────────────────────────────
  const deleteSelected = useCallback((sn, se) => {
    if (sn) {
      const newEdges = edges.filter(e=>e.from!==sn&&e.to!==sn);
      const newNodes = nodes.filter(n=>n.id!==sn);
      setEdges(newEdges); setNodes(newNodes); setSelNode(null);
      pushHistory(newNodes, newEdges);
      swal.success("Node deleted.");
    } else if (se) {
      const newEdges = edges.filter(e=>e.id!==se);
      setEdges(newEdges); setSelEdge(null);
      pushHistory(nodes, newEdges);
      swal.success("Connection deleted.");
    }
  }, [nodes, edges, pushHistory]);

  // ── Keyboard shortcuts ──────────────────────────────────────
  const snRef=useRef(selNode), seRef=useRef(selEdge);
  useEffect(()=>{snRef.current=selNode;},[selNode]);
  useEffect(()=>{seRef.current=selEdge;},[selEdge]);

  useEffect(()=>{
    const fn=e=>{
      const tag=document.activeElement.tagName;
      if (tag==="TEXTAREA"||tag==="INPUT") return;
      if (e.key==="Delete"||e.key==="Backspace") deleteSelected(snRef.current,seRef.current);
      if ((e.metaKey||e.ctrlKey)&&e.key==="z"&&!e.shiftKey) { e.preventDefault(); undo(); }
      if ((e.metaKey||e.ctrlKey)&&(e.key==="y"||(e.shiftKey&&e.key==="z"))) { e.preventDefault(); redo(); }
      if ((e.metaKey||e.ctrlKey)&&e.key==="d") { e.preventDefault(); duplicateNode(); }
      if ((e.metaKey||e.ctrlKey)&&e.key==="s") { e.preventDefault(); setShowSave(true); }
    };
    window.addEventListener("keydown",fn);
    return()=>window.removeEventListener("keydown",fn);
  },[deleteSelected, undo, redo, duplicateNode]);

  const changeColor = color => {
    if (!selNode) return;
    const newNodes = nodes.map(n=>n.id===selNode?{...n,color}:n);
    setNodes(newNodes);
    pushHistory(newNodes, edges);
  };

  const getSVGPt = e => {
    const r=svgRef.current.getBoundingClientRect();
    return { x:(e.clientX-r.left-pan.x)/zoom, y:(e.clientY-r.top-pan.y)/zoom };
  };

  // ─── Pointer events ───────────────────────────────────────
  const onNodePointerDown = useCallback((e, node) => {
    e.stopPropagation();
    // FIX #2: Connect mode (Shift OR connectMode toggle)
    if (e.shiftKey || connectMode) {
      setConn(node); setSelNode(null); setSelEdge(null);
      svgRef.current.setPointerCapture(e.pointerId);
      return;
    }
    setSelNode(node.id); setSelEdge(null);
    const pt=getSVGPt(e);
    setDrag({nodeId:node.id, ox:pt.x-node.x, oy:pt.y-node.y});
    svgRef.current.setPointerCapture(e.pointerId);
  }, [pan, zoom, connectMode]);

  const onSVGDown = e => {
    if (e.target===svgRef.current || e.target.tagName==="rect") {
      setSelNode(null); setSelEdge(null);
      if (e.altKey || e.button===1) {
        setPanDrag({sx:e.clientX-pan.x, sy:e.clientY-pan.y});
        svgRef.current.setPointerCapture(e.pointerId);
      }
    }
  };

  const onSVGMove = e => {
    const r=svgRef.current.getBoundingClientRect();
    const pt={x:(e.clientX-r.left-pan.x)/zoom, y:(e.clientY-r.top-pan.y)/zoom};
    setMouse(pt);
    if (panDrag) { setPan({x:e.clientX-panDrag.sx, y:e.clientY-panDrag.sy}); return; }
    if (drag) setNodes(ns=>ns.map(n=>n.id===drag.nodeId?{...n,x:pt.x-drag.ox,y:pt.y-drag.oy}:n));
  };

  const onSVGUp = e => {
    if (connecting) {
      const r=svgRef.current.getBoundingClientRect();
      const pt={x:(e.clientX-r.left-pan.x)/zoom, y:(e.clientY-r.top-pan.y)/zoom};
      const tgt=nodes.find(n=>hitTest(n,pt.x,pt.y)&&n.id!==connecting.id);
      if (tgt && !edges.some(ed=>ed.from===connecting.id&&ed.to===tgt.id)) {
        const newEdge = {id:uid(), from:connecting.id, to:tgt.id, label:""};
        const newEdges = [...edges, newEdge];
        setEdges(newEdges);
        pushHistory(nodes, newEdges);
        swal.success("Nodes connected!");
      } else if (!tgt) {
        swal.warning("Drop on another node to connect!");
      }
      setConn(null);
    }
    // Commit drag to history
    if (drag) pushHistory(nodes, edges);
    setDrag(null); setPanDrag(null);
  };

  useEffect(()=>{
    const el=svgRef.current;
    const fn=e=>{
      e.preventDefault();
      setZoom(z=>Math.min(Math.max(z*(e.deltaY>0?.9:1.1),.2),4));
    };
    el.addEventListener("wheel",fn,{passive:false});
    return()=>el.removeEventListener("wheel",fn);
  },[]);

  // ─── Edit node label ──────────────────────────────────────
  const openEdit = () => {
    if (!selNode) return;
    const n=nodes.find(n=>n.id===selNode);
    if (!n) return;
    // FIX #1: show the actual display form (convert \n literal to real newlines for textarea)
    setEditVal(n.label.replace(/\\n/g, "\n"));
    setEditModal(selNode);
  };

  const saveEdit = () => {
    // FIX #1: normalize newlines back to \n literal on save
    const normalized = normalizeLabel(editVal);
    const newNodes = nodes.map(n=>n.id===editModal?{...n,label:normalized}:n);
    setNodes(newNodes);
    pushHistory(newNodes, edges);
    setEditModal(null);
    swal.success("Label saved!");
  };

  // ─── FIX #4: Edit edge label ──────────────────────────────
  const openEditEdge = async (edgeId, currentLabel) => {
    const result = await swal.inputEdgeLabel(currentLabel);
    if (!result.isConfirmed) return;
    const newEdges = edges.map(e=>e.id===edgeId?{...e,label:result.value?.trim()||""}:e);
    setEdges(newEdges);
    pushHistory(nodes, newEdges);
    swal.success("Edge label updated!");
  };

  // ─── Node double-click opens edit ─────────────────────────
  const onNodeDblClick = (nodeId) => {
    setSelNode(nodeId); setSelEdge(null);
    const n=nodes.find(n=>n.id===nodeId);
    if (!n) return;
    setEditVal(n.label.replace(/\\n/g, "\n"));
    setEditModal(nodeId);
  };

  // ─── FIX #5: Export SVG/PNG with background ───────────────
  const exportSVG = () => {
    const svgEl = svgRef.current;
    const cloned = svgEl.cloneNode(true);
    cloned.setAttribute("xmlns","http://www.w3.org/2000/svg");
    const bbox = svgEl.getBBox ? svgEl.getBBox() : {x:0,y:0,width:1800,height:1000};
    cloned.setAttribute("viewBox",`0 0 ${svgEl.clientWidth} ${svgEl.clientHeight}`);
    const blob = new Blob([cloned.outerHTML],{type:"image/svg+xml"});
    const a=document.createElement("a");
    a.href=URL.createObjectURL(blob);
    a.download="flowchart.svg";
    a.click();
    swal.success("SVG downloaded!");
  };

  const exportPNG = () => {
    const svgEl = svgRef.current;
    const W=svgEl.clientWidth, H=svgEl.clientHeight;
    const cloned = svgEl.cloneNode(true);
    cloned.setAttribute("xmlns","http://www.w3.org/2000/svg");
    cloned.setAttribute("width",W);
    cloned.setAttribute("height",H);
    const svg64 = btoa(unescape(encodeURIComponent(new XMLSerializer().serializeToString(cloned))));
    const cv=document.createElement("canvas");
    cv.width=W*2; cv.height=H*2; // 2x for retina
    const ctx=cv.getContext("2d");
    ctx.scale(2,2);
    ctx.fillStyle=T.bg;
    ctx.fillRect(0,0,W,H);
    const img=new Image();
    img.onload=()=>{
      ctx.drawImage(img,0,0,W,H);
      const a=document.createElement("a");
      a.download="flowchart.png";
      a.href=cv.toDataURL("image/png");
      a.click();
      swal.success("PNG downloaded!");
    };
    img.src="data:image/svg+xml;base64,"+svg64;
  };

  // ─── FIX #6: Fit to screen ────────────────────────────────
  const fitToScreen = () => {
    if (nodes.length===0) return;
    const el=svgRef.current;
    const W=el.clientWidth, H=el.clientHeight;
    const xs=nodes.map(n=>n.x), ys=nodes.map(n=>n.y);
    const minX=Math.min(...xs)-20, minY=Math.min(...ys)-20;
    const maxX=Math.max(...xs)+NODE_W+20, maxY=Math.max(...ys)+NODE_H+20;
    const newZoom=Math.min((W/(maxX-minX)),(H/(maxY-minY)),.95+.05);
    const clampedZoom=Math.min(Math.max(newZoom,.2),4);
    setZoom(clampedZoom);
    setPan({x:W/2-(minX+maxX)/2*clampedZoom, y:H/2-(minY+maxY)/2*clampedZoom});
    swal.info("Fitted to screen!");
  };

  const sno=nodes.find(n=>n.id===selNode);
  const seo=edges.find(e=>e.id===selEdge);
  const canDel=!!(selNode||selEdge);

  const SEC={fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:2.5,margin:"14px 0 6px",fontWeight:600};
  const DIV={height:1,background:T.border,margin:"10px 0"};
  const PL={fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:2,marginBottom:3,marginTop:10};
  const PV={fontSize:12,color:T.text,marginBottom:2};

  return (
    <div ref={rootRef} style={{display:"flex",flexDirection:"column",height:"100vh",background:T.bg,color:T.text,fontFamily:"'Inter','Segoe UI',sans-serif",overflow:"hidden"}}>

      {/* ─── HEADER ─── */}
      <div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 20px",background:T.surface,borderBottom:`1px solid ${T.border}`,flexShrink:0,flexWrap:"wrap"}}>
        <div style={{display:"flex",flexDirection:"column"}}>
          <span style={{fontWeight:800,fontSize:16,color:T.accent,letterSpacing:-.5}}>FLOWCRAFT</span>
          {currentTitle && <span style={{fontSize:10,color:T.muted,marginTop:-2}}>{currentId ? `#${currentId} · ` : ""}{currentTitle}</span>}
        </div>

        {/* Logged-in user badge */}
        <div style={{display:"flex",alignItems:"center",gap:7,padding:"5px 12px",borderRadius:99,background:T.panel,border:`1px solid ${T.border}`}}>
          <div style={{width:22,height:22,borderRadius:"50%",background:T.accent,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,color:"#fff",flexShrink:0}}>
            {loggedInUser.fullName.charAt(0).toUpperCase()}
          </div>
          <div style={{display:"flex",flexDirection:"column",lineHeight:1.2}}>
            <span style={{fontSize:11,fontWeight:600,color:T.text}}>{loggedInUser.fullName}</span>
            <span style={{fontSize:9,color:T.muted,textTransform:"capitalize"}}>{loggedInUser.role}</span>
          </div>
        </div>
        <div style={{flex:1}}/>
        {localSaved&&<span style={{fontSize:10,color:T.success,display:"flex",alignItems:"center",gap:4}}>
          <span style={{width:6,height:6,borderRadius:"50%",background:T.success,display:"inline-block"}}/>Saved locally
        </span>}

        {/* Undo/Redo */}
        <button title="Undo (Ctrl+Z)" style={{...mkBtn(),fontSize:11,padding:"5px 10px"}} onClick={undo} disabled={histIdx<=0}>↩ Undo</button>
        <button title="Redo (Ctrl+Y)" style={{...mkBtn(),fontSize:11,padding:"5px 10px"}} onClick={redo} disabled={histIdx>=history.length-1}>↪ Redo</button>

        <div style={{width:1,height:20,background:T.border}}/>
        <button style={{...mkBtn(T.accent),display:"flex",alignItems:"center",gap:5}} onClick={()=>setShowSave(true)}>
          💾 {currentId?"Update":"Save to DB"}
        </button>
        <button style={{...mkBtn(T.success,true)}} onClick={()=>setShowLoad(true)}>
          📂 Load
        </button>
        <div style={{width:1,height:20,background:T.border}}/>
        <button style={{...mkBtn(),fontSize:11,padding:"5px 10px"}} onClick={exportSVG}>⬇ SVG</button>
        <button style={{...mkBtn(),fontSize:11,padding:"5px 10px"}} onClick={exportPNG}>⬇ PNG</button>
        <div style={{width:1,height:20,background:T.border}}/>
        <button style={{...mkBtn()}} onClick={async ()=>{
          const r = await swal.confirmClear();
          if(r.isConfirmed){
            setNodes([]); setEdges([]); setSelNode(null); setSelEdge(null);
            setCurrentId(null); setCurrentTitle("Untitled Flowchart");
            pushHistory([],[]);
            swal.success("Canvas cleared!");
          }
        }}>↺ New</button>
        <button style={{...mkBtn(isFullscreen?T.accent:undefined)}} onClick={toggleFullscreen}>
          {isFullscreen?"✕ Exit":"⤢ Full"}
        </button>
      </div>

      <div style={{display:"flex",flex:1,overflow:"hidden"}}>

        {/* ─── LEFT SIDEBAR ─── */}
        <div style={{width:200,background:T.surface,borderRight:`1px solid ${T.border}`,padding:"12px 12px",display:"flex",flexDirection:"column",gap:4,overflowY:"auto",flexShrink:0}}>
          <div style={SEC}>Add Node</div>
          {Object.entries(NODE_TYPES).map(([type,info])=>(
            <button key={type} onClick={()=>addNode(type)}
              style={{display:"flex",alignItems:"center",gap:9,padding:"8px 12px",borderRadius:6,border:`1px solid ${T.border}`,background:T.panel,cursor:"pointer",fontSize:12,color:T.text,fontFamily:"inherit",fontWeight:500,textAlign:"left"}}
              onMouseEnter={e=>{e.currentTarget.style.borderColor=T.accent;e.currentTarget.style.background="#1c2d4a";}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor=T.border;e.currentTarget.style.background=T.panel;}}>
              <span style={{fontSize:13,color:T.muted}}>{info.icon}</span>{info.label}
            </button>
          ))}

          <div style={DIV}/>
          <div style={SEC}>Actions</div>

          {/* FIX #2: Connect Mode Toggle */}
          <button
            style={{...mkBtn(connectMode?T.warning:T.accent, !connectMode),width:"100%",
              background:connectMode?T.warning+"22":"transparent",
              border:`1px solid ${connectMode?T.warning:T.accent}`}}
            onClick={()=>{setConnectMode(c=>!c); swal.info(connectMode?"Connect mode OFF":"Connect mode ON — click source then target");}}>
            {connectMode ? "🔗 Connecting..." : "🔗 Connect Mode"}
          </button>

          <button style={{...mkBtn(T.accent,true),opacity:selNode?1:.3,cursor:selNode?"pointer":"default",width:"100%"}} onClick={openEdit} disabled={!selNode}>
            ✏ Edit Label
          </button>

          {/* FIX #3: Duplicate Node */}
          <button style={{...mkBtn(T.accent,true),opacity:selNode?1:.3,cursor:selNode?"pointer":"default",width:"100%"}} onClick={duplicateNode} disabled={!selNode}>
            ⧉ Duplicate Node
          </button>

          <button style={{...mkBtn(T.danger),opacity:canDel?1:.3,cursor:canDel?"pointer":"default",width:"100%",background:canDel?T.danger:T.panel}}
            onClick={()=>deleteSelected(selNode,selEdge)} disabled={!canDel}>
            🗑 Delete {selNode?"Node":selEdge?"Edge":""}
          </button>

          <div style={DIV}/>
          <div style={SEC}>View</div>
          <div style={{display:"flex",gap:5}}>
            <button style={{...mkBtn(),flex:1}} onClick={()=>setZoom(z=>Math.min(z*1.2,4))}>+</button>
            <button style={{...mkBtn(),flex:1}} onClick={()=>setZoom(1)}>1:1</button>
            <button style={{...mkBtn(),flex:1}} onClick={()=>setZoom(z=>Math.max(z*0.8,.2))}>−</button>
          </div>
          <button style={{...mkBtn(),width:"100%",marginTop:4}} onClick={fitToScreen}>⊡ Fit Screen</button>

          <div style={DIV}/>
          <div style={SEC}>Tips</div>
          <div style={{fontSize:10,color:T.muted,opacity:.7,lineHeight:2.1}}>
            • Click = select<br/>
            • Drag = move<br/>
            • <b style={{color:T.accent}}>Shift+drag</b> = connect<br/>
            • <b style={{color:T.warning}}>Connect Mode</b> = click to link<br/>
            • Double-click = edit label<br/>
            • Alt+drag = pan<br/>
            • Scroll = zoom<br/>
            • Del = delete<br/>
            • Ctrl+Z = undo<br/>
            • Ctrl+D = duplicate<br/>
            • Ctrl+S = save
          </div>
        </div>

        {/* ─── CANVAS ─── */}
        <div style={{flex:1,position:"relative",overflow:"hidden"}}>
          <svg ref={svgRef} width="100%" height="100%"
            onPointerDown={onSVGDown}
            onPointerMove={onSVGMove}
            onPointerUp={onSVGUp}
            style={{cursor:connecting?"crosshair":connectMode?"cell":panDrag?"grabbing":"default",display:"block"}}>
            <defs>
              <pattern id="grid" width="24" height="24" patternUnits="userSpaceOnUse" patternTransform={`translate(${pan.x%24},${pan.y%24})`}>
                <circle cx=".8" cy=".8" r=".8" fill={T.subtle} opacity=".5"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill={T.bg}/>
            <rect width="100%" height="100%" fill="url(#grid)"/>
            <g transform={`translate(${pan.x},${pan.y}) scale(${zoom})`}>
              {/* FIX #4: edges with double-click to edit label */}
              {edges.map(e=>(
                <Arrow key={e.id} edge={e} nodes={nodes}
                  selected={e.id===selEdge}
                  onSelect={id=>{setSelEdge(id);setSelNode(null);}}
                  onDoubleClick={openEditEdge}/>
              ))}
              {connecting&&(
                <line
                  x1={getCenter(connecting).x} y1={getCenter(connecting).y}
                  x2={mouse.x} y2={mouse.y}
                  stroke={T.accent} strokeWidth={2} strokeDasharray="6 3"
                  style={{pointerEvents:"none"}}/>
              )}
              {nodes.map(n=>(
                <FlowNode key={n.id} node={n} selected={n.id===selNode}
                  onPointerDown={onNodePointerDown}
                  onDoubleClick={onNodeDblClick}/>
              ))}
            </g>
          </svg>

          {/* Connect mode overlay hint */}
          {connectMode && !connecting && (
            <div style={{position:"absolute",top:12,left:"50%",transform:"translateX(-50%)",background:T.warning+"22",border:`1px solid ${T.warning}`,borderRadius:20,padding:"5px 18px",fontSize:12,color:T.warning,pointerEvents:"none"}}>
              🔗 Click a node to start connecting
            </div>
          )}
          {connectMode && connecting && (
            <div style={{position:"absolute",top:12,left:"50%",transform:"translateX(-50%)",background:T.accent+"22",border:`1px solid ${T.accent}`,borderRadius:20,padding:"5px 18px",fontSize:12,color:T.accent,pointerEvents:"none"}}>
              → Now click the target node
            </div>
          )}

          {nodes.length===0&&(
            <div style={{position:"absolute",inset:0,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",pointerEvents:"none"}}>
              <div style={{fontSize:48,opacity:.05,marginBottom:12}}>◈</div>
              <div style={{fontSize:14,color:T.subtle,fontWeight:600}}>Canvas is empty</div>
              <div style={{fontSize:12,color:T.border,marginTop:6}}>Add nodes from the left, or 📂 Load a saved flowchart</div>
            </div>
          )}

          {currentId&&(
            <div style={{position:"absolute",top:12,left:"50%",transform:"translateX(-50%)",background:T.surface,border:`1px solid ${T.border}`,borderRadius:20,padding:"4px 16px",fontSize:12,color:T.muted,pointerEvents:"none",display:"flex",alignItems:"center",gap:6}}>
              <span style={{width:6,height:6,borderRadius:"50%",background:T.success,display:"inline-block"}}/>
              {currentTitle}
            </div>
          )}

          <div style={{position:"absolute",bottom:14,left:14,fontSize:11,color:T.muted,background:T.surface,padding:"3px 10px",borderRadius:5,border:`1px solid ${T.border}`}}>
            {Math.round(zoom*100)}% · {nodes.length}n {edges.length}e · {histIdx}/{history.length-1} history
          </div>
        </div>

        {/* ─── RIGHT PANEL ─── */}
        <div style={{width:215,background:T.surface,borderLeft:`1px solid ${T.border}`,padding:"12px 14px",overflowY:"auto",flexShrink:0}}>
          <div style={{fontSize:10,fontWeight:700,color:T.accent,letterSpacing:2,textTransform:"uppercase",marginBottom:12}}>Properties</div>

          {sno ? (
            <>
              <div style={PL}>Type</div>
              <span style={{display:"inline-flex",alignItems:"center",gap:6,padding:"3px 10px",borderRadius:99,background:T.panel,border:`1px solid ${T.border}`,fontSize:12,marginBottom:8}}>
                {NODE_TYPES[sno.type].icon} {sno.type}
              </span>

              <div style={PL}>Label</div>
              <div style={{...PV,marginBottom:8,wordBreak:"break-all",whiteSpace:"pre-wrap"}}>
                {sno.label.replace(/\\n/g,"\n")}
              </div>

              <div style={PL}>Position</div>
              <div style={{fontSize:11,color:T.muted,marginBottom:8}}>x:{Math.round(sno.x)}, y:{Math.round(sno.y)}</div>

              <div style={PL}>Connections</div>
              <div style={{fontSize:12,color:T.muted,marginBottom:10}}>{edges.filter(e=>e.from===selNode||e.to===selNode).length}</div>

              <div style={DIV}/>
              <div style={PL}>Node Color</div>
              <div style={{marginTop:8,marginBottom:10}}>
                <ColorPicker current={sno.color||"#8b949e"} onChange={changeColor}/>
              </div>

              <div style={DIV}/>
              <button style={{...mkBtn(T.accent,true),width:"100%",marginBottom:6}} onClick={openEdit}>✏ Edit Label</button>
              <button style={{...mkBtn(T.accent,true),width:"100%",marginBottom:6}} onClick={duplicateNode}>⧉ Duplicate</button>
              <button style={{...mkBtn(T.danger),width:"100%",background:T.danger}} onClick={()=>deleteSelected(selNode,null)}>🗑 Delete Node</button>
            </>
          ) : seo ? (
            <>
              <div style={PL}>From</div>
              <div style={{...PV,marginBottom:8}}>{nodes.find(n=>n.id===seo.from)?.label.replace(/\\n/g," ")||"?"}</div>

              <div style={PL}>To</div>
              <div style={{...PV,marginBottom:8}}>{nodes.find(n=>n.id===seo.to)?.label.replace(/\\n/g," ")||"?"}</div>

              <div style={PL}>Label</div>
              <div style={{...PV,marginBottom:10,color:seo.label?T.text:T.muted,fontStyle:seo.label?"normal":"italic"}}>
                {seo.label||"(none)"}
              </div>

              <div style={DIV}/>
              {/* FIX #4: Edit edge label from panel */}
              <button style={{...mkBtn(T.accent,true),width:"100%",marginBottom:6}} onClick={()=>openEditEdge(seo.id, seo.label)}>
                ✏ Edit Edge Label
              </button>
              <button style={{...mkBtn(T.danger),width:"100%",background:T.danger}} onClick={()=>deleteSelected(null,selEdge)}>🗑 Delete Edge</button>
            </>
          ) : (
            <>
              <div style={{fontSize:11,color:T.subtle,lineHeight:2,marginTop:4}}>Select a node or edge to view properties.</div>
              {currentId&&(
                <div style={{marginTop:16,padding:"10px 12px",borderRadius:8,background:T.panel,border:`1px solid ${T.border}`}}>
                  <div style={{fontSize:9,color:T.muted,textTransform:"uppercase",letterSpacing:2,marginBottom:6}}>Current File</div>
                  <div style={{fontSize:12,color:T.text,fontWeight:600}}>{currentTitle}</div>
                  <div style={{fontSize:10,color:T.muted,marginTop:3}}>ID #{currentId}</div>
                  <button style={{...mkBtn(T.accent),width:"100%",marginTop:10,fontSize:11}} onClick={()=>setShowSave(true)}>
                    🔄 Update in DB
                  </button>
                </div>
              )}
            </>
          )}

          <div style={{...DIV,marginTop:16}}/>
          <div style={SEC}>Legend</div>
          {Object.entries(NODE_TYPES).map(([type,info])=>(
            <div key={type} style={{display:"flex",alignItems:"center",gap:8,marginBottom:7}}>
              <div style={{width:10,height:10,borderRadius:type==="decision"?0:2,background:T.muted,transform:type==="decision"?"rotate(45deg)":"none",flexShrink:0}}/>
              <span style={{fontSize:11,color:T.muted}}>{info.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ─── MODALS ─── */}
      {showSave&&(
        <SaveModal nodes={nodes} edges={edges} folders={folders}
          currentId={currentId} currentTitle={currentTitle}
          onClose={()=>setShowSave(false)}
          onSaved={(id,title)=>{setCurrentId(id);setCurrentTitle(title);}}/>
      )}
      {showLoad&&(
        <LoadModal onClose={()=>setShowLoad(false)} onLoad={handleLoad}/>
      )}

      {/* ─── EDIT LABEL MODAL ─── */}
      {editModal&&(
        <div style={{position:"fixed",inset:0,background:"#00000088",display:"flex",alignItems:"center",justifyContent:"center",zIndex:200}}
          onClick={()=>setEditModal(null)}>
          <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:10,padding:24,minWidth:320,boxShadow:"0 20px 60px #000000aa"}}
            onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:14,fontWeight:700,color:T.accent,marginBottom:14}}>✏ Edit Label</div>
            <textarea
              style={{width:"100%",minHeight:80,background:T.panel,border:`1px solid ${T.border}`,borderRadius:6,color:T.text,padding:"8px 12px",fontSize:13,fontFamily:"'Inter',sans-serif",resize:"vertical",boxSizing:"border-box",outline:"none"}}
              value={editVal}
              onChange={e=>setEditVal(e.target.value)}
              autoFocus
              onKeyDown={e=>{
                if(e.key==="Enter"&&e.ctrlKey) saveEdit();
                if(e.key==="Escape") setEditModal(null);
              }}
              placeholder="Label text (press Enter for new line)"/>
            <div style={{fontSize:10,color:T.muted,margin:"6px 0 14px"}}>Ctrl+Enter to save · Esc to cancel · Enter = new line</div>
            <div style={{display:"flex",gap:8}}>
              <button style={{...mkBtn(T.accent),flex:1}} onClick={saveEdit}>✔ Save</button>
              <button style={{...mkBtn(),flex:1}} onClick={()=>setEditModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}